/**
 * "Become a referrer" — lets an existing applicant apply for a referrer
 * profile through the SAME onboarding wizard + approval flow as new
 * referrer signups.
 *
 * Flow states (driven by the applicant's referrer doc, if any):
 * - 'none'     → no referrer application yet: show the entry-point card.
 * - 'pending'  → application under review: show the "under review" state
 *                INSTEAD of the entry point (a second submission is
 *                impossible — the onboard endpoint upserts in place and the
 *                UI never offers the CTA).
 * - 'rejected' → the applicant may re-apply: entry point shown again.
 * - 'active'   → approved: the card hides; dual applicant+referrer roles are
 *                handled by the mandatory role selection at login and the
 *                in-app role switcher.
 *
 * Server-side companion rules (server.ts):
 * - POST /api/referrer/onboard is idempotent: a pending doc is updated in
 *   place, a rejected doc returns to pending, only an ACTIVE doc 409s.
 * - On org approval, an applicant's pre-existing 'applicant' role is
 *   preserved in users/{uid}.additionalRoles so the applicant dashboard
 *   stays reachable and role selection offers both roles.
 * - On rejection of an applicant's referrer application, the referrer
 *   linkage markers (claims + referrerId/referrerStatus) are stripped so
 *   the account returns to a pure applicant state and the entry point
 *   shows again.
 * - POST /api/auth/validate-role already recognizes the dual-role user:
 *   'applicant' is universally allowed and 'referrer' is granted by the
 *   referrer custom claim OR a matching referrers doc — both set on
 *   approval. No tamper bypass: options still come from server truth.
 * - Draft sessions persist server-side (`users/{uid}.onboardingDrafts`):
 *   a started-but-unfinished application shows "Continue setup" and the
 *   wizard restores the saved answers + step. See serverOnboardingDraft.ts.
 * - The card FAILS CLOSED: when the profile lookup errors (anything other
 *   than a definitive 404), it renders nothing instead of guessing "no
 *   application" and offering onboarding to an existing referrer.
 */

export type ReferrerApplicationState = 'none' | 'pending' | 'rejected' | 'active' | 'loading';

/**
 * Maps a referrer doc status (or absence of a doc) to the card state.
 * Unknown/empty statuses are treated as pending — the safe direction is
 * "under review", never a duplicate application.
 */
export function resolveReferrerApplicationState(doc: { status?: string } | null | undefined): Exclude<ReferrerApplicationState, 'loading'> {
  if (!doc) return 'none';
  const s = String(doc.status || '').toLowerCase();
  // Suspended / deactivated referrers still HOLD a referrer profile — they
  // are referrers (the referrer view surfaces the suspension), so they must
  // never be offered a fresh application either.
  if (s === 'active' || s === 'approved' || s === 'suspended' || s === 'inactive' || s === 'deactivated' || s === 'disabled') return 'active';
  if (s === 'rejected') return 'rejected';
  return 'pending';
}

/**
 * Full card state for the applicant dashboard, adding the two states the
 * bare application mapping cannot express:
 * - 'in-progress' → an onboarding draft exists (started, never submitted):
 *   the CTA must RESUME the saved session, never restart it.
 * - 'unknown'     → the profile lookup FAILED (network/server), as opposed
 *   to a definitive 404. Fail-closed: render no entry point at all. An
 *   existing referrer must never be offered onboarding because a fetch
 *   blipped — that fail-open path was the re-trigger bug.
 */
export type BecomeReferrerCardState = ReferrerApplicationState | 'in-progress' | 'unknown';

export function resolveBecomeReferrerCardState(input: {
  doc: { status?: string } | null | undefined;
  /** A saved onboarding draft with real content exists for this user. */
  draftPresent: boolean;
  /** The referrer-profile request failed (NOT a definitive "no doc"). */
  profileFetchFailed: boolean;
  /**
   * The app itself granted this user a referrer ROLE OPTION (server-truth
   * detection: referrer claim and/or a referrers doc found by the role
   * resolver). Such a user already has referrer-side access, so the entry
   * point must NEVER render for them — regardless of what this card's own
   * profile fetch returns. (Live bug: an approved referrer kept seeing
   * "Become a referrer" with a working start button that restarted
   * onboarding and allowed a duplicate submission.)
   */
  hasReferrerRole?: boolean;
}): BecomeReferrerCardState {
  if (input.profileFetchFailed) return 'unknown';
  const appState = resolveReferrerApplicationState(input.doc);
  if (input.hasReferrerRole) {
    // Role holder: only the informational under-review state may show
    // (that's the post-submit confirmation). Everything else — entry,
    // resume, re-apply — is suppressed; the referrer view owns the rest.
    return appState === 'pending' ? 'pending' : 'active';
  }
  // A submitted application always wins over a leftover draft: 'pending',
  // 'rejected' and 'active' are states of the APPLICATION, and a stale
  // draft must never resurrect a "continue setup" CTA over them.
  if (appState !== 'none') return appState;
  return input.draftPresent ? 'in-progress' : 'none';
}

/**
 * Whether the entry-point CTA may be offered for the given state.
 * Only applicants with no application, a rejected one, or a saved draft
 * (resume) may start. 'unknown' never offers anything.
 */
export function canStartReferrerApplication(state: ReferrerApplicationState | BecomeReferrerCardState): boolean {
  return state === 'none' || state === 'rejected' || state === 'in-progress';
}

/**
 * Derives the dual-role applicant flag from a user profile doc.
 * An approved applicant-turned-referrer keeps 'applicant' in
 * `additionalRoles` (written at org-approval time); detection must not
 * rely on the scalar `role` field alone, which mutateUserRole sets to the
 * org role.
 */
export function hasApplicantRole(profile: {
  role?: string;
  primaryRole?: string;
  workspaceMembers?: Record<string, unknown>;
  additionalRoles?: unknown;
}): boolean {
  const pRole = String(profile.role || '').toLowerCase();
  if (pRole === 'applicant') return true;
  const members = profile.workspaceMembers || {};
  if (Object.values(members).some((v) => String((v as any)?.role ?? v).toLowerCase() === 'applicant')) return true;
  const additional = profile.additionalRoles;
  if (Array.isArray(additional) && additional.map(String).map((s) => s.toLowerCase()).includes('applicant')) return true;
  return false;
}
